import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["childMarginInlineStart", "children", "childTruncateLength", "displayName", "href", "isEditable", "lookup", "onClick", "showTooltip", "size", "socialNetwork", "tooltipPlacement", "tooltipTitle", "type"];
import { Component } from 'react';
import shallowCompare from 'react-addons-shallow-compare';
import { isTrellisTheme, useTheme } from 'foundations-theming/utils';
import { getAvatarResolver } from './lib/AvatarResolverWithSource';
import defaultFetchAvatars from './api/defaultFetchAvatars';
import { AVATAR_SIZES, DEFAULT_PROPS } from './Constants';
import { getAvatarSrc } from './lib/getAvatarSrc';
import defaultPortalId from './lib/defaultPortalId';
import { fetchImage, isFetched } from './lib/Image';
import LookupRecord from './Records/LookupRecord';
import { AvatarDisplay } from './AvatarDisplay';
import { getMarginProps } from './lib/stylePropHelpers';
import { getBorderProps } from './lib/stylePropHelpers';
import { AVATAR_SOURCES } from './lib/AvatarSources';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_LOOKUP_RECORD = new LookupRecord();
export class AvatarRequestInternal extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      lookupRecord: DEFAULT_LOOKUP_RECORD,
      avatarURI: undefined,
      source: null,
      error: undefined,
      loaded: false
    };
    this._isMounted = false;
    this.handleResolverUpdate = () => {
      this.updateAvatarUrl(this.props);
    };
    this.startWatching = () => {
      getAvatarResolver().watch(this.handleResolverUpdate);
    };
    this.stopWatching = () => {
      getAvatarResolver().unwatch(this.handleResolverUpdate);
    };
    this.updateAvatarUrl = props => {
      const {
        lookup
      } = props;
      if (!lookup.primaryIdentifier || !lookup.type || typeof lookup.primaryIdentifier === 'string' && lookup.primaryIdentifier.length < 1 || 'deal' === lookup.type || 'ticket' === lookup.type) {
        this.setState({
          loaded: true,
          avatarURI: undefined,
          source: AVATAR_SOURCES.UNKNOWN
        });
        return undefined;
      }
      const sizePx = props.size !== 'responsive' ? AVATAR_SIZES[props.size] : AVATAR_SIZES.xl;
      const lookupRecordFromProps = new LookupRecord(Object.assign({}, lookup, {
        portalId: props.portalId,
        dimensions: sizePx
      }));
      const lookupRecord = this.state.lookupRecord.equals(lookupRecordFromProps) ? this.state.lookupRecord : lookupRecordFromProps;
      const {
        uri: avatarURI,
        source
      } = getAvatarResolver().getAvatarCacheEntry(lookupRecord, props.fetchAvatars, props.portalId, props.trellisDefault);
      const fetched = isFetched(avatarURI);
      const isDefault = typeof avatarURI !== 'string';
      const loaded = isDefault || !!fetched;
      if (this.state.avatarURI === avatarURI) {
        return null;
      }
      this.setState({
        avatarURI,
        lookupRecord,
        error: undefined,
        loaded,
        source
      });
      if (loaded) {
        return null;
      }

      // Forces the browser to load the image into the cache to avoid UI-jitters
      // as the image loads in.

      fetchImage(avatarURI).then(() => {
        if (this._isMounted && this.state.avatarURI === avatarURI) {
          this.setState({
            loaded: true
          });
        }
      }).catch(error => {
        if (this._isMounted && this.state.avatarURI === avatarURI) {
          this.setState({
            error
          });
        }
      });
      return null;
    };
  }
  componentDidMount() {
    this._isMounted = true;
    this.startWatching();
    this.updateAvatarUrl(this.props);
  }
  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }
  componentDidUpdate() {
    this.updateAvatarUrl(this.props);
  }
  componentWillUnmount() {
    this._isMounted = false;
    this.stopWatching();
  }
  render() {
    const _this$props = this.props,
      {
        childMarginInlineStart,
        children,
        childTruncateLength,
        displayName,
        href,
        isEditable,
        lookup,
        onClick,
        showTooltip,
        size,
        socialNetwork,
        tooltipPlacement,
        tooltipTitle,
        type
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const borderProps = getBorderProps(rest);
    const marginProps = getMarginProps(rest);
    const {
      src,
      source
    } = getAvatarSrc({
      avatarURI: this.state.avatarURI,
      error: this.state.error,
      loaded: this.state.loaded,
      src: this.props.src,
      source: this.state.source
    });
    const isLoaded = this.state.loaded && (!!this.state.source || !!this.props.src);
    return /*#__PURE__*/_jsx(AvatarDisplay, Object.assign({}, Object.assign({
      'data-test-id': this.props['data-test-id'],
      childMarginInlineStart,
      children,
      childTruncateLength,
      displayName,
      href,
      isEditable,
      onClick,
      showTooltip,
      size,
      socialNetwork,
      src,
      tooltipPlacement,
      tooltipTitle,
      type: type || lookup.type,
      isLoaded,
      source
    }, borderProps, marginProps)));
  }
}
AvatarRequestInternal.defaultProps = Object.assign({}, DEFAULT_PROPS, {
  portalId: defaultPortalId(),
  fetchAvatars: defaultFetchAvatars
});
export function AvatarRequest(props) {
  const theme = useTheme();
  const trellisDefault = isTrellisTheme(theme);
  return /*#__PURE__*/_jsx(AvatarRequestInternal, Object.assign({}, props, {
    trellisDefault: trellisDefault
  }));
}